import React from 'react';
import SeoMeta from '../../components/SeoMeta/SeoMeta';
import IvoriSiteHeader from '../ivori-landing/IvoriSiteHeader';
import IvoriSiteFooter from '../ivori-landing/IvoriSiteFooter';
import IvoriLandingChatPrompt from '../ivori-landing/IvoriLandingChatPrompt';
import IvoriDecisionBoard from '../ivori-landing/IvoriDecisionBoard';
import IvoriMarketplaceGrid from '../ivori-landing/IvoriMarketplaceGrid';
import useFadeInOnView from '../ivori-landing/useFadeInOnView';
import './CSuiteLandingPage.css';

const RESEARCH_CARDS = [
  {
    index: '01 — Causal AI',
    label: 'Cause, not correlation',
    desc: 'Trained to model what actually drives an outcome rather than what merely accompanies it — so a recommendation reflects a mechanism, not a pattern-match.',
  },
  {
    index: '02 — Counterfactual Reasoning',
    label: 'Every path not taken',
    desc: 'The model runs the decision against its plausible alternatives in parallel, comparing outcomes before it commits to a read — the way a strong operator already thinks, formalized.',
  },
  {
    index: '03 — Decision Science',
    label: 'Formal, not intuitive',
    desc: 'Grounded in the expected-value and trade-off frameworks used in real strategic, capital, and policy work — not generic advice dressed up as analysis.',
  },
  {
    index: '04 — Continuous Calibration',
    label: 'Checked against reality',
    desc: 'Recalibrated as outcomes unfold rather than fixed at a single training run, so its confidence tracks how the world actually moves, not a snapshot of it.',
  },
];

export default function CSuiteLandingPage() {
  const heroRef = useFadeInOnView();
  const demoRef = useFadeInOnView();
  const boardRef = useFadeInOnView();
  const researchRef = useFadeInOnView();
  const marketplaceHeadRef = useFadeInOnView();
  const marketplaceGridRef = useFadeInOnView();

  return (
    <div className="ivori-landing-root">
      <SeoMeta
        title="ivori C-Suite — Executive Simulation & Decision Intelligence"
        description="Model any move. Command any room. IVORI C-Suite is the executive simulation and decision intelligence platform built on the Decision World Model."
        keywords="IVORI C-Suite, executive simulation, decision intelligence, board simulations, strategic decisions, causal AI"
        url="https://csuite.ivoriai.com"
      />

      <IvoriSiteHeader />

      <section className="ivori-landing-hero" id="top">
        <div className="ivori-landing-grain" />
        <div className="ivori-landing-hero-inner ivori-landing-fade-up" ref={heroRef}>
          <div className="ivori-landing-eyebrow">AI for Decision-Makers</div>
          <h1>Give it a decision. See how it plays out.</h1>
          <a
            className="ivori-landing-hero-cta"
            href="#board"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('board')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            See it in motion
          </a>
          <div className="ivori-landing-hero-powered">Powered by the Decision World Model</div>
        </div>
      </section>

      <IvoriLandingChatPrompt sectionRef={demoRef} />

      <IvoriDecisionBoard sectionRef={boardRef} />

      <section className="ivori-landing-research-section" id="research">
        <div className="ivori-landing-grain" />
        <div className="ivori-landing-research-inner ivori-landing-fade-up" ref={researchRef}>
          <div className="ivori-landing-research-head">
            <div className="ivori-landing-section-eyebrow">The Research</div>
            <div className="ivori-landing-demo-title" style={{ marginBottom: 0 }}>
              Built to reason under uncertainty — not to guess.
            </div>
            <p className="ivori-landing-research-lede">
              The Decision World Model isn't a chatbot fine-tuned to sound confident. It's built on decision
              science and causal inference, trained to hold multiple possible futures at once and reason about
              which choice actually changes the outcome — the same discipline a serious strategy, policy, or
              research function runs by hand, at machine speed.
            </p>
          </div>

          <div className="ivori-landing-research-grid">
            {RESEARCH_CARDS.map((card) => (
              <div className="ivori-landing-research-card" key={card.index}>
                <div className="ivori-landing-research-index">{card.index}</div>
                <div className="ivori-landing-research-label">{card.label}</div>
                <p className="ivori-landing-research-desc">{card.desc}</p>
              </div>
            ))}
          </div>

          <p className="ivori-landing-research-close">
            This is why it sits on a C-suite desk rather than a support queue — it's built for decisions with
            real, compounding consequences.
          </p>
        </div>
      </section>

      <section className="ivori-landing-section" id="marketplace">
        <div className="ivori-landing-section-head ivori-landing-fade-up" ref={marketplaceHeadRef}>
          <div className="ivori-landing-section-eyebrow">Marketplace</div>
          <div className="ivori-landing-section-title">Apps built on the decision model</div>
        </div>

        <IvoriMarketplaceGrid gridRef={marketplaceGridRef} />
      </section>

      <IvoriSiteFooter />
    </div>
  );
}
